import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Button, Modal, Form, Table, Badge, Alert, Row, Col, Card, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionGroupsService from "../../services/api/permissionGroupsService";
import PermissionsViewModal from "../../components/modals/PermissionsViewModal";
import { getReadablePermissions } from "../../utils/permissionDescriptions";
import styles from './PermissionGroupsPage.module.scss';

/**
 * 🧩 Página de Gerenciamento de Grupos de Permissões
 * 
 * Permite ao produtor criar e gerenciar grupos de permissões para colaboradores.
 * Oferece blocos modulares predefinidos que podem ser combinados.
 */
const PermissionGroupsPage = () => {
  const { isOwner } = usePermissions();
  
  // Estados principais
  const [groups, setGroups] = useState([]);
  const [systemBlocks, setSystemBlocks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  
  // Estado do modal de visualização de permissões
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);
  
  // Estado para controlar cards expandidos
  const [expandedCards, setExpandedCards] = useState({});
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: {},
    selectedBlocks: []
  });
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    if (isOwner()) {
      loadGroups();
      loadSystemBlocks();
      loadStats();
    }
  }, []);

  // Carregar grupos
  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await PermissionGroupsService.getGroups(showInactive);
      setGroups(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      toast.error('Erro ao carregar grupos de permissões');
    } finally {
      setLoading(false);
    }
  };

  // Carregar blocos do sistema
  const loadSystemBlocks = async () => {
    try {
      console.log('🧱 Carregando blocos do sistema...');
      const response = await PermissionGroupsService.getSystemBlocks();
      console.log('✅ Blocos carregados:', response);
      console.log('📦 Dados dos blocos:', response.data);
      setSystemBlocks(response.data || []);
      console.log('✅ systemBlocks atualizado com', response.data?.length || 0, 'blocos');
    } catch (error) {
      console.error('❌ Erro ao carregar blocos:', error);
      console.error('❌ Detalhes do erro:', error.response?.data || error.message);
      toast.error('Erro ao carregar blocos de acesso. Tente novamente.');
    }
  };

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      const response = await PermissionGroupsService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Recarregar quando showInactive mudar
  useEffect(() => {
    if (isOwner()) {
      loadGroups();
    }
  }, [showInactive]);

  // Abrir modal para criar/editar
  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name || "",
        description: group.description || "",
        permissions: typeof group.permissions === 'string' 
          ? JSON.parse(group.permissions) 
          : group.permissions || {},
        selectedBlocks: []
      });
    } else {
      setEditingGroup(null);
      setFormData({
        name: "",
        description: "",
        permissions: {},
        selectedBlocks: []
      });
    }
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormData({
      name: "",
      description: "",
      permissions: {},
      selectedBlocks: []
    });
  };

  // Selecionar/desselecionar bloco e atualizar permissões automaticamente
  const handleToggleBlock = async (blockType) => {
    const newSelectedBlocks = formData.selectedBlocks.includes(blockType)
      ? formData.selectedBlocks.filter(type => type !== blockType)
      : [...formData.selectedBlocks, blockType];

    setFormData(prev => ({
      ...prev,
      selectedBlocks: newSelectedBlocks
    }));

    // Combinar permissões dos blocos selecionados
    if (newSelectedBlocks.length > 0) {
      try {
        const response = await PermissionGroupsService.combineBlocks(newSelectedBlocks);
        setFormData(prev => ({
          ...prev,
          permissions: response.data,
          selectedBlocks: newSelectedBlocks
        }));
      } catch (error) {
        console.error('Erro ao combinar blocos:', error);
        toast.error('Erro ao combinar blocos');
      }
    } else {
      // Se nenhum bloco selecionado, limpar permissões
      setFormData(prev => ({
        ...prev,
        permissions: {},
        selectedBlocks: []
      }));
    }
  };

  // Salvar grupo
  const handleSaveGroup = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Nome do grupo é obrigatório');
      return;
    }

    if (Object.keys(formData.permissions).length === 0) {
      toast.error('Selecione pelo menos uma permissão ou combine blocos');
      return;
    }

    try {
      if (editingGroup) {
        await PermissionGroupsService.updateGroup(editingGroup.id, {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions
        });
        toast.success('Grupo atualizado com sucesso!');
      } else {
        await PermissionGroupsService.createGroup({
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions
        });
        toast.success('Grupo criado com sucesso!');
      }
      
      handleCloseModal();
      loadGroups();
      loadStats();
    } catch (error) {
      console.error('Erro ao salvar grupo:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar grupo');
    }
  };

  // Abrir modal de visualização de permissões
  const handleViewPermissions = (group) => {
    setViewingGroup(group);
    setShowViewModal(true);
  };

  // Fechar modal de visualização
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingGroup(null);
  };

  // Toggle para expandir/recolher card
  const toggleCardExpansion = (blockType, e) => {
    e.stopPropagation(); // Evitar que o clique selecione o bloco
    setExpandedCards(prev => ({
      ...prev,
      [blockType]: !prev[blockType]
    }));
  };

  // Deletar grupo
  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Tem certeza que deseja deletar o grupo "${groupName}"?`)) {
      return;
    }

    try {
      await PermissionGroupsService.deleteGroup(groupId);
      toast.success('Grupo deletado com sucesso!');
      loadGroups();
      loadStats();
    } catch (error) {
      console.error('Erro ao deletar grupo:', error);
      toast.error(error.response?.data?.message || 'Erro ao deletar grupo');
    }
  };

  // Filtrar grupos
  const filteredGroups = groups.filter(group => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        group.name.toLowerCase().includes(search) ||
        (group.description && group.description.toLowerCase().includes(search))
      );
    }
    return true;
  });

  // Contagem de permissões
  const countPermissions = (permissions) => {
    let count = 0;
    Object.values(permissions).forEach(module => {
      if (typeof module === 'object') {
        count += Object.values(module).filter(value => value === true).length;
      }
    });
    return count;
  };

  // Obter ícone do bloco
  const getBlockIcon = (blockType) => {
    const icons = {
      'interno': 'mdi:view-dashboard',
      'cobranca': 'mdi:currency-usd',
      'gestor_pedidos': 'mdi:package-variant',
      'mentorado_atendimento': 'mdi:account-group',
      'checkout_produto': 'mdi:cart',
      'administrador_produtor': 'mdi:shield-crown',
      'produtos_privados': 'mdi:lock'
    };
    return icons[blockType] || 'mdi:puzzle';
  };

  // Formatar permissões para tooltip formatado
  const formatPermissionsTooltip = (permissions) => {
    const readablePermissions = getReadablePermissions(permissions);
    
    if (readablePermissions.length === 0) {
      return 'Nenhuma permissão configurada';
    }

    return (
      <div style={{ textAlign: 'left', maxWidth: '350px' }}>
        {readablePermissions.map((module, index) => (
          <div key={module.module} style={{ marginBottom: index < readablePermissions.length - 1 ? '12px' : 0 }}>
            <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '6px', color: '#fff' }}>
              {module.title}
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: '1.6' }}>
              {module.actions.map((action) => (
                <li key={action.key} style={{ marginBottom: '4px' }}>
                  {action.description}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  // Verificar se não é owner
  if (!isOwner()) {
    return (
      <div className="container py-5">
        <Alert variant="warning">
          <Icon icon="mdi:lock" className="me-2" />
          Apenas produtores podem acessar esta página.
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span style={{ fontSize: '22px', fontWeight: '600', display: 'block' }}>
                🧩 Grupos de Permissões
              </span>
              <span style={{ fontSize: '14px', color: '#6c757d', display: 'block', marginTop: '4px' }}>
                Gerencie grupos de permissões para seus colaboradores
              </span>
            </div>
            <Button 
              variant="primary" 
              onClick={() => handleOpenModal()}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Icon icon="mdi:plus" style={{ fontSize: '20px' }} />
              Novo Grupo
            </Button>
          </div>
        </Col>
      </Row>

      {/* Estatísticas */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center" style={{ borderLeft: '4px solid #0d6efd' }}>
              <Card.Body>
                <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#0d6efd', display: 'block' }}>
                  {stats.total_groups}
                </span>
                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Total de Grupos
                </span>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center" style={{ borderLeft: '4px solid #198754' }}>
              <Card.Body>
                <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#198754', display: 'block' }}>
                  {stats.active_groups}
                </span>
                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Grupos Ativos
                </span>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center" style={{ borderLeft: '4px solid #ffc107' }}>
              <Card.Body>
                <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffc107', display: 'block' }}>
                  {stats.inactive_groups}
                </span>
                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Grupos Inativos
                </span>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center" style={{ borderLeft: '4px solid #6f42c1' }}>
              <Card.Body>
                <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#6f42c1', display: 'block' }}>
                  {stats.total_collaborators}
                </span>
                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Colaboradores
                </span>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={10}>
              <Form.Control
                type="text"
                placeholder="🔍 Buscar por nome ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Check
                type="checkbox"
                label="Mostrar inativos"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabela de Grupos */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2">Carregando grupos...</div>
            </div>
          ) : filteredGroups.length === 0 ? (
            <Alert variant="info" className="text-center">
              <Icon icon="mdi:information" className="me-2" />
              {searchTerm 
                ? 'Nenhum grupo encontrado com este filtro.' 
                : 'Nenhum grupo criado ainda. Clique em "Novo Grupo" para começar.'}
            </Alert>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Nome</th>
                  <th style={{ width: '25%' }}>Descrição</th>
                  <th style={{ width: '12%' }} className="text-center">Permissões</th>
                  <th style={{ width: '12%' }} className="text-center">Colaboradores</th>
                  <th style={{ width: '10%' }} className="text-center">Status</th>
                  <th style={{ width: '8%' }} className="text-center">Ver Detalhes</th>
                  <th style={{ width: '13%' }} className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => (
                  <tr key={group.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <Icon icon="mdi:account-group" className="me-2" style={{ fontSize: '20px', color: '#0d6efd' }} />
                        <span style={{ fontWeight: '500' }}>{group.name}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: '13px', color: '#6c757d' }}>
                        {group.description || '—'}
                      </span>
                    </td>
                    <td className="text-center">
                      <Badge bg="info" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                        {countPermissions(group.permissions)}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="secondary">
                        {group.collaborators_count || 0}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg={group.is_active ? 'success' : 'warning'}>
                        {group.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="info"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPermissions(group);
                        }}
                        title="Ver detalhes das permissões"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          borderRadius: '50%'
                        }}
                      >
                        <Icon icon="mdi:information" style={{ fontSize: '18px' }} />
                      </Button>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(group);
                        }}
                        title="Editar"
                      >
                        <Icon icon="mdi:pencil" style={{ fontSize: '18px' }} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGroup(group.id, group.name);
                        }}
                        title="Deletar"
                        disabled={group.collaborators_count > 0}
                      >
                        <Icon icon="mdi:delete" style={{ fontSize: '18px' }} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Criar/Editar Grupo */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        size="lg"
        className={styles.permissionGroupModal}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <Icon 
              icon={editingGroup ? "mdi:pencil" : "mdi:plus-circle"} 
              className={styles.titleIcon} 
            />
            {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveGroup}>
          <Modal.Body className={styles.modalBody}>
            <p className={styles.modalSubtitle}>
              Crie grupos de permissões para organizar o acesso dos seus colaboradores às diferentes áreas da plataforma.
            </p>

            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:text" /> Nome do Grupo *
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Financeiro, Atendimento, Analista..."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className={styles.input}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:file-document-edit" /> Descrição
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Descreva as responsabilidades deste grupo..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={styles.textarea}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Form.Label className={styles.label} style={{ marginBottom: 0 }}>
                  <Icon icon="mdi:puzzle" /> Blocos de Acesso *
                </Form.Label>
                {systemBlocks.length === 0 && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={loadSystemBlocks}
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    <Icon icon="mdi:refresh" className="me-1" />
                    Recarregar Blocos
                  </Button>
                )}
              </div>
              <p className={styles.helperText}>
                Selecione um ou mais blocos para combinar suas permissões automaticamente
              </p>
              {systemBlocks.length === 0 ? (
                <Alert variant="warning" className="mb-3">
                  <Icon icon="mdi:alert" className="me-2" />
                  Nenhum bloco de acesso disponível. Clique em "Recarregar Blocos" para tentar novamente.
                </Alert>
              ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                gap: '16px',
                marginBottom: '20px'
              }}>
                {systemBlocks.map((block) => {
                  const isSelected = formData.selectedBlocks.includes(block.block_type);
                  const readablePerms = getReadablePermissions(block.permissions);
                  const permCount = countPermissions(block.permissions);
                  const isExpanded = expandedCards[block.block_type] || false;
                  const hasMoreModules = readablePerms.length > 2;
                  const modulesToShow = isExpanded ? readablePerms : readablePerms.slice(0, 2);
                  
                  return (
                    <div
                      key={block.block_type}
                      onClick={() => handleToggleBlock(block.block_type)}
                      className={styles.blockSelectionCard}
                      data-selected={isSelected}
                      style={{
                        border: isSelected ? '2px solid #0d6efd' : '1px solid #dee2e6',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#e7f1ff' : '#fff',
                        transition: 'all 0.3s ease',
                        boxShadow: isSelected ? '0 4px 12px rgba(13, 110, 253, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                        position: 'relative',
                        minHeight: isExpanded ? 'auto' : '180px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* Checkbox no canto */}
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: isSelected ? '2px solid #0d6efd' : '2px solid #dee2e6',
                        backgroundColor: isSelected ? '#0d6efd' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}>
                        {isSelected && <Icon icon="mdi:check" style={{ color: '#fff', fontSize: '16px' }} />}
                      </div>

                      {/* Header com ícone e nome */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '12px',
                        paddingRight: '30px'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: isSelected ? '#0d6efd' : '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}>
                          <Icon 
                            icon={getBlockIcon(block.block_type)} 
                            style={{ 
                              fontSize: '24px', 
                              color: isSelected ? '#fff' : '#0d6efd'
                            }} 
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 600, 
                            fontSize: '15px',
                            color: isSelected ? '#0d6efd' : '#212529',
                            lineHeight: '1.3'
                          }}>
                            {block.name}
                          </div>
                        </div>
                      </div>

                      {/* Resumo das permissões */}
                      <div style={{ 
                        fontSize: '12px',
                        color: '#6c757d',
                        lineHeight: '1.6',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {modulesToShow.map((module) => (
                          <div key={module.module}>
                            <strong style={{ color: isSelected ? '#0d6efd' : '#495057', display: 'block', marginBottom: '3px' }}>
                              {module.title}:
                            </strong>
                            <div style={{ 
                              fontSize: '11px', 
                              color: isSelected ? '#495057' : '#6c757d',
                              lineHeight: '1.5'
                            }}>
                              {module.actions.map((action, idx) => (
                                <span key={action.key}>
                                  {action.description}
                                  {idx < module.actions.length - 1 ? '. ' : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                        {hasMoreModules && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={(e) => toggleCardExpansion(block.block_type, e)}
                            style={{
                              fontSize: '11px',
                              padding: '4px 8px',
                              textDecoration: 'none',
                              color: isSelected ? '#0d6efd' : '#6c757d',
                              fontWeight: 500,
                              alignSelf: 'flex-start',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {isExpanded ? (
                              <>
                                <Icon icon="mdi:chevron-up" width={16} />
                                Ver menos
                              </>
                            ) : (
                              <>
                                <Icon icon="mdi:chevron-down" width={16} />
                                Ver mais ({readablePerms.length - 2} {readablePerms.length - 2 === 1 ? 'módulo' : 'módulos'})
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      {/* Badge total */}
                      <div style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Badge 
                          bg={isSelected ? 'primary' : 'secondary'} 
                          style={{ fontSize: '11px', fontWeight: 500 }}
                        >
                          {permCount} {permCount === 1 ? 'permissão' : 'permissões'}
                        </Badge>
                        <span style={{ 
                          fontSize: '11px', 
                          color: isSelected ? '#0d6efd' : '#6c757d',
                          fontWeight: 500
                        }}>
                          {isSelected ? '✓ Selecionado' : 'Clique para selecionar'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </Form.Group>

            {Object.keys(formData.permissions).length === 0 && (
              <Alert variant="info" className="mb-0">
                <Icon icon="mdi:information" className="me-2" />
                Selecione pelo menos um bloco de acesso para definir as permissões
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button 
              variant="secondary" 
              onClick={handleCloseModal}
              className={styles.cancelButton}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              className={styles.submitButton}
            >
              <Icon icon={editingGroup ? "mdi:content-save" : "mdi:plus"} className="me-1" />
              {editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de Visualização de Permissões */}
      <PermissionsViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        group={viewingGroup}
      />

    </div>
  );
};

export default PermissionGroupsPage;

